import {
  BookmarkIcon,
  CheckIcon,
  ClipboardDocumentListIcon,
  MinusIcon,
  PlusIcon,
  UserPlusIcon,
} from "@heroicons/react/24/outline";
import { BookmarkIcon as BookmarkIconSolid } from "@heroicons/react/24/solid";
import CustomButton from "../CustomButton";
import { useNavigate } from "react-router-dom";

/**
 * Selection toolbar for Vendor Discovery — mirrors Innovation Lab /providers.
 * Select All is shown whenever selection is enabled; My Vendors CTAs when the
 * sponsor can manage My Vendors; Compare when Trial Experience is enabled.
 */
export default function VendorDiscoverySelectionBar({
  canSelect = false,
  canManageMyVendors = false,
  canCompare = false,
  handleSelectAllCards,
  allCardsSelected = false,
  listSelectedVendors = [],
  onRemove,
  onCreateList,
  onAddToList,
  onAddToMyVendors,
  onBookmarkSelected,
  allSelectedBookmarked = false,
  isBookmarkSelectedPending = false,
}) {
  const navigate = useNavigate();

  if (!canSelect) return null;

  const hasSelection = listSelectedVendors.length > 0;

  return (
    <>
      <button
        type="button"
        onClick={handleSelectAllCards}
        className="inline-flex flex-shrink-0 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
      >
        {allCardsSelected ? (
          <MinusIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
        ) : (
          <CheckIcon className="h-4 w-4 text-gray-500" aria-hidden="true" />
        )}
        {allCardsSelected ? "Deselect All" : "Select All"}
      </button>

      {canManageMyVendors && hasSelection ? (
        <>
          <button
            type="button"
            onClick={onCreateList}
            className="inline-flex flex-shrink-0 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            Create List
          </button>
          <button
            type="button"
            onClick={onAddToList}
            className="inline-flex flex-shrink-0 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <ClipboardDocumentListIcon className="h-4 w-4" aria-hidden="true" />
            Add to List
          </button>
          <button
            type="button"
            onClick={onAddToMyVendors}
            className="inline-flex flex-shrink-0 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
            title="Add the selected vendors to My Vendors"
          >
            <UserPlusIcon className="h-4 w-4 text-indigo-600" aria-hidden="true" />
            Add to My Vendors
          </button>
          <button
            type="button"
            onClick={onBookmarkSelected}
            disabled={isBookmarkSelectedPending}
            className="inline-flex flex-shrink-0 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            title={
              allSelectedBookmarked
                ? "Remove bookmark from all selected vendors"
                : "Bookmark all selected vendors"
            }
          >
            {allSelectedBookmarked ? (
              <BookmarkIconSolid className="h-4 w-4 text-indigo-600" aria-hidden="true" />
            ) : (
              <BookmarkIcon className="h-4 w-4" aria-hidden="true" />
            )}
            {allSelectedBookmarked ? "Unbookmark Selected" : "Bookmark Selected"}
          </button>
        </>
      ) : null}

      {canCompare && hasSelection ? (
        <CustomButton
          color="bg-indigo-600"
          size="text-base"
          weight="font-normal"
          height="h-9"
          width="w-[82px]"
          textColor="text-white"
          text="Compare"
          onClick={() => navigate("/TrialExperience")}
        />
      ) : null}

      {hasSelection ? (
        <div className="flex h-[40px] min-w-0 flex-1 items-center overflow-x-auto">
          <div className="flex flex-nowrap items-center gap-2">
            {listSelectedVendors.map((vendor) => (
              <span
                key={vendor.id || vendor.name}
                className="inline-flex flex-shrink-0 items-center gap-x-1.5 whitespace-nowrap rounded-md bg-indigo-600 px-2 py-1 text-xs font-medium text-white ring-1 ring-indigo-600"
              >
                {vendor.name}
                <button
                  type="button"
                  className="group relative -mr-1 ml-1 h-3.5 w-3.5 rounded-sm hover:bg-indigo-800/20"
                  onClick={() => onRemove?.(vendor)}
                  aria-label={`Remove ${vendor.name}`}
                >
                  <svg viewBox="0 0 14 14" className="h-3.5 w-3.5 stroke-current" aria-hidden="true">
                    <path d="M4 4l6 6m0-6l-6 6" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex-1" />
      )}
    </>
  );
}
